import React, { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// Third Party Libraries
import DatePicker from '~/components/General/OlcDatePicker';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

//Action
import { SET_CONTACTS_FILTERS, CLEAR_CONTACTS_FILTERS } from '~/redux/actions/action-types';
import { failure } from '~/redux/actions/snackbar-actions';

//Utils
import { MESSAGES } from '~/utils/message';
import {
  DEFAULT_PAGINATION,
  ADDRESS_STATUS,
  MAILED_STATUS,
  DATE_REGEX,
  CONTACT_TYPE,
} from '~/utils/constants';
import {
  dateProtector,
  handleRawChangeForDatePicker,
  formatDateOnly,
  parseLocalDateOnly,
} from '~/utils/date-format';

// Components
// import MultiSelect from './MultiSelect';
import MultiSelect from '~/components/General/MultiSelectNew';

// MUI Components
import IconButton from '@mui/material/IconButton';
import { Box, Grid, InputLabel, Input, InputAdornment } from '@mui/material';

// Icons
import { MultiSelectArrowCIcon } from '~/assets/images';
import CloseIcon from '@mui/icons-material/Close';

// Styles
import './styles.scss';

const AdvanceFilters = forwardRef((props, ref) => {
  const {
    handleCloseFilter,
    open,
    updatePagination,
    handleApplyFiltersToUrl,
    handleClearFiltersFromUrl,
  } = props;
  const [selectedMailedStatus, setSelectedMailedStatus] = useState([]);
  const [selectedContactType, setSelectedContactType] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedAddressStatus, setSelectedAddressStatus] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [createdDateRange, setCreatedDateRange] = useState([null, null]);
  const [qrCodeDateRange, setQrCodeDateRange] = useState([null, null]);
  const [showActiveMailings, setShowActiveMailings] = useState(false);
  const [startDate, endDate] = dateRange;
  const [startCreatedDate, endCreatedDate] = createdDateRange;
  const [startQrCodeDate, endQrCodeDate] = qrCodeDateRange;
  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
  const [createdDatepickerOpen, setCreatedDatepickerOpen] = useState(false);
  const [qrCodeScanDatepickerOpen, setQrCodeScanDatepickerOpen] = useState(false);
  const [propertyAddress, setPropertyAddress] = useState('');

  const [sortedTags, setSortedTags] = useState([]);
  const previousMode = useRef(null);

  const dispatch = useDispatch();

  const tags = useSelector((state) => state.contactsReducers.labels);

  const perPage = useSelector(
    (state) => state.contactsReducers.perPage || DEFAULT_PAGINATION.pageSize
  );

  const currentMode = useSelector((state) =>
    state.userReducers.user?.isLiveMode === true
      ? true
      : state.userReducers.user?.isLiveMode === false
        ? false
        : null
  );

  const filters = useSelector((state) => state.contactsReducers.filters);

  const onClose = () => {
    clearFilters(true);
  };

  const clearFilters = (persist = false) => {
    setSelectedMailedStatus([]);
    setSortedTags(
      tags.map((tag) => {
        return {
          ...tag,
          disabled: false,
        };
      })
    );
    setSelectedTags([]);
    setPropertyAddress('');
    setSelectedAddressStatus([]);
    setDateRange([null, null]);
    setCreatedDateRange([null, null]);
    setQrCodeDateRange([null, null]);
    setShowActiveMailings(false);
    setSelectedContactType([]);
    if (!persist) {
      const didUpdateUrl = handleClearFiltersFromUrl?.();

      if (!didUpdateUrl) {
        dispatch({ type: CLEAR_CONTACTS_FILTERS });
      }
    }
    handleCloseFilter();
  };

  const removeTag = (tag) => {
    setSelectedTags((selectedTags) =>
      selectedTags.filter((item) => {
        return item !== tag;
      })
    );
    setSortedTags(
      sortedTags.map((e) => {
        return {
          ...e,
          disabled: false,
        };
      })
    );
  };

  const handleTagsSelection = (selectedTag) => {
    if (Array.isArray(selectedTag)) {
      setSelectedTags(selectedTag);
    }
  };

  const applyFilters = () => {
    const lastMailedDate =
      startDate && endDate
        ? {
            startDate: formatDateOnly(startDate),
            endDate: formatDateOnly(endDate),
          }
        : null;

    if (startDate && endDate === null) {
      dispatch(failure(MESSAGES.CONTACTS.END_DATE_REQUIRED));
      return;
    }

    const createdDate =
      startCreatedDate && endCreatedDate
        ? {
            startDate: formatDateOnly(startCreatedDate),
            endDate: formatDateOnly(endCreatedDate),
          }
        : null;

    const qrCodeScanDate =
      startQrCodeDate && endQrCodeDate
        ? {
            startDate: formatDateOnly(startQrCodeDate),
            endDate: formatDateOnly(endQrCodeDate),
          }
        : null;

    if (startQrCodeDate && endQrCodeDate === null) {
      dispatch(failure(MESSAGES.CONTACTS.END_DATE_REQUIRED));
      return;
    }

    let payload = {
      applyFilters: true,
      filters: {
        tags: selectedTags,
        addressStatus: selectedAddressStatus,
        lastMailedStatus: selectedMailedStatus,
        contactType: selectedContactType,
        activeMailing: showActiveMailings,
        lastMailedDate: lastMailedDate,
        createdAt: createdDate,
        lastQrScannedDate: qrCodeScanDate,
        ...(propertyAddress ? { propertyAddress } : {}),
      },
    };

    const filtersWithValues = Object.keys(payload.filters).filter((key) => {
      const value = payload.filters[key];
      if (typeof value === 'boolean') return value === true;
      return Array.isArray(value) ? value.length > 0 : value !== null;
    });

    payload.filterSize = filtersWithValues.length;

    updatePagination({
      pageSize: perPage,
      page: 1,
    });

    if (filtersWithValues.length <= 0) {
      dispatch(failure(MESSAGES.CONTACTS.FILTERS_REQUIRED));
      return;
    }

    const didUpdateUrl = handleApplyFiltersToUrl?.(payload);

    if (!didUpdateUrl) {
      dispatch({ type: SET_CONTACTS_FILTERS, payload: payload });
    }
    onClose();
  };

  const isAnyFilterApplied = () => {
    return (
      selectedTags.length > 0 ||
      selectedAddressStatus.length > 0 ||
      selectedMailedStatus.length > 0 ||
      selectedContactType.length > 0 ||
      showActiveMailings ||
      startDate ||
      endDate ||
      startCreatedDate ||
      endCreatedDate ||
      startQrCodeDate ||
      endQrCodeDate ||
      propertyAddress?.trim() !== ''
    );
  };

  useImperativeHandle(ref, () => ({
    clearFilters() {
      clearFilters();
    },
  }));

  useEffect(() => {
    if (currentMode === null) {
      return;
    }

    if (previousMode.current === null || previousMode.current === currentMode) {
      previousMode.current = currentMode;
      return;
    }

    previousMode.current = currentMode;
    clearFilters();
  }, [currentMode]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedMailedStatus(filters.lastMailedStatus || []);
    setSelectedTags((filters.tags || []).filter((tag) => tag !== -1));
    setSelectedAddressStatus(filters.addressStatus || []);
    setPropertyAddress(filters.propertyAddress || '');
    setSelectedContactType(filters.contactType || []);
    setShowActiveMailings(filters.activeMailing === true);
    setDateRange(
      filters.lastMailedDate
        ? [
            parseLocalDateOnly(filters.lastMailedDate.startDate),
            parseLocalDateOnly(filters.lastMailedDate.endDate),
          ]
        : [null, null]
    );
    setCreatedDateRange(
      filters.createdAt
        ? [
            parseLocalDateOnly(filters.createdAt.startDate),
            parseLocalDateOnly(filters.createdAt.endDate),
          ]
        : [null, null]
    );
    setQrCodeDateRange(
      filters.lastQrScannedDate
        ? [
            parseLocalDateOnly(filters.lastQrScannedDate.startDate),
            parseLocalDateOnly(filters.lastQrScannedDate.endDate),
          ]
        : [null, null]
    );
  }, [filters, open]);

  useEffect(() => {
    const newTags = tags.sort((a, b) => a?.title.localeCompare(b?.title));
    setSortedTags(
      newTags.map((tag) => {
        return {
          ...tag,
          disabled: false,
        };
      })
    );
  }, [tags]);

  return (
    <OlcFormModal
      open={open}
      onClose={onClose}
      title={MESSAGES.CONTACTS.ADVANCE_FILTERS.HEADING}
      className="contactsFilterModalWrapper"
      contentClassName="contactsFilterContent"
      footer={
        <>
          <OlcButton variant="secondary" onClick={() => clearFilters()}>
            {MESSAGES.CONTACTS.ADVANCE_FILTERS.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton variant="primary" onClick={applyFilters} disabled={!isAnyFilterApplied()}>
            {MESSAGES.CONTACTS.ADVANCE_FILTERS.SUBMIT_BUTTON}
          </OlcButton>
        </>
      }
    >
      <Box className="filterTagsWrapper">
        <Grid container spacing={1}>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <InputLabel className="tagsLabel">
              {MESSAGES.CONTACTS.ADVANCE_FILTERS.TAGS_LABEL}
            </InputLabel>
            <MultiSelect
              component={'tag'}
              options={sortedTags}
              selectedValue={selectedTags}
              setSelectedValue={handleTagsSelection}
              placeHolderText={MESSAGES.CONTACTS.ADVANCE_FILTERS.SEARCH_TAG}
              noOptionText={MESSAGES.CONTACTS.ADVANCE_FILTERS.NO_SEARCH_TAG}
              enabledSearch={true}
              newDesign={true}
              clearable={true}
            />
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="addressMailWrapper">
              <Box className="selectAddress">
                <InputLabel className="addressLabel">
                  {MESSAGES.CONTACTS.ADVANCE_FILTERS.ADDRESS_STATUS}
                </InputLabel>
                <MultiSelect
                  options={ADDRESS_STATUS}
                  selectedValue={selectedAddressStatus}
                  setSelectedValue={setSelectedAddressStatus}
                  placeHolderText={MESSAGES.CONTACTS.ADVANCE_FILTERS.ADDRESS_STATUS}
                  noOptionText={MESSAGES.CONTACTS.ADVANCE_FILTERS.NO_ADDRESS_STATUS}
                  clearable={true}
                  newDesign={true}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="addressMailWrapper">
              <Box className="selectAddress">
                <InputLabel className="addressLabel newfieldsLabel">
                  {MESSAGES.CONTACTS.ADVANCE_FILTERS.MAILED_STATUS}
                </InputLabel>
                <MultiSelect
                  options={MAILED_STATUS}
                  selectedValue={selectedMailedStatus}
                  setSelectedValue={setSelectedMailedStatus}
                  placeHolderText={MESSAGES.CONTACTS.ADVANCE_FILTERS.MAILED_STATUS}
                  noOptionText={MESSAGES.CONTACTS.ADVANCE_FILTERS.NO_MAILED_STATUS}
                  clearable={true}
                  newDesign={true}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="mailedDate newDatePicker updatedDesign">
              <Box className="selectDate">
                <InputLabel className="dateLabel newfieldsLabel">
                  {MESSAGES.CONTACTS.ADVANCE_FILTERS.MAILED_DATE}
                </InputLabel>
                <img
                  src={MultiSelectArrowCIcon}
                  alt="arrow"
                  style={{ transform: isDatepickerOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
                <DatePicker
                  minDate={null}
                  onCalendarClose={() => setIsDatepickerOpen(false)}
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    dateProtector(update);
                    setDateRange(update);
                  }}
                  onChangeRaw={(e) => {
                    if (e.target.value === '' || DATE_REGEX.test(e.target.value)) {
                      handleRawChangeForDatePicker(e, setDateRange);
                    } else {
                      e.preventDefault();
                    }
                  }}
                  onFocus={() => setIsDatepickerOpen(true)}
                  onBlur={(e) => {
                    if (e.target.value == '') {
                      setDateRange([null, null]);
                    }
                    setIsDatepickerOpen(false);
                  }}
                  placeholderText={`${MESSAGES.CONTACTS.ADVANCE_FILTERS.MAILED_DATE}`}
                  isClearable
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="mailedDate newDatePicker updatedDesign">
              <Box className="selectDate">
                <InputLabel className="dateLabel newfieldsLabel">
                  {MESSAGES.CONTACTS.ADVANCE_FILTERS.CREATED_DATE}
                </InputLabel>
                <img
                  src={MultiSelectArrowCIcon}
                  alt="arrow"
                  style={{
                    transform: createdDatepickerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
                <DatePicker
                  minDate={null}
                  onCalendarClose={() => setCreatedDatepickerOpen(false)}
                  selectsRange={true}
                  startDate={startCreatedDate}
                  endDate={endCreatedDate}
                  onChange={(update) => {
                    dateProtector(update);
                    setCreatedDateRange(update);
                  }}
                  onChangeRaw={(e) => {
                    if (e.target.value === '' || DATE_REGEX.test(e.target.value)) {
                      handleRawChangeForDatePicker(e, setCreatedDateRange);
                    } else {
                      e.preventDefault();
                    }
                  }}
                  onFocus={() => setCreatedDatepickerOpen(true)}
                  onBlur={() => setCreatedDatepickerOpen(false)}
                  placeholderText={`${MESSAGES.CONTACTS.ADVANCE_FILTERS.CREATED_DATE}`}
                  isClearable
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="mailedDate newDatePicker updatedDesign">
              <Box className="selectDate">
                <InputLabel className="dateLabel newfieldsLabel">
                  {MESSAGES.CONTACTS.ADVANCE_FILTERS.QR_SCAN_DATE}
                </InputLabel>
                <img
                  src={MultiSelectArrowCIcon}
                  alt="arrow"
                  style={{
                    transform: qrCodeScanDatepickerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
                <DatePicker
                  minDate={null}
                  onCalendarClose={() => setQrCodeScanDatepickerOpen(false)}
                  selectsRange={true}
                  startDate={startQrCodeDate}
                  endDate={endQrCodeDate}
                  onChange={(update) => {
                    dateProtector(update);
                    setQrCodeDateRange(update);
                  }}
                  onChangeRaw={(e) => {
                    if (e.target.value === '' || DATE_REGEX.test(e.target.value)) {
                      handleRawChangeForDatePicker(e, setQrCodeDateRange);
                    } else {
                      e.preventDefault();
                    }
                  }}
                  onFocus={() => setQrCodeScanDatepickerOpen(true)}
                  onBlur={() => setQrCodeScanDatepickerOpen(false)}
                  placeholderText={`${MESSAGES.CONTACTS.ADVANCE_FILTERS.QR_SCAN_DATE}`}
                  isClearable
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="newAddressMailWrapper">
              <Box className="propertyAddress">
                <InputLabel className="dateLabel newfieldsLabel">
                  {MESSAGES.ORDERS.ADVANCE_FILTERS.PROPERTY_ADDRESS}
                </InputLabel>
                <Input
                  className="createdByInput newfieldsInput"
                  value={propertyAddress}
                  name="searchPropertyAddress"
                  autoComplete="off"
                  onChange={(e) => setPropertyAddress(e.target.value)}
                  type="text"
                  placeholder={MESSAGES.ORDERS.ADVANCE_FILTERS.PROPERTY_ADDRESS}
                  sx={{
                    border: '0.5px solid #303030',
                    width: '100%',
                    padding: '0px 16px',
                    fontSize: '14px',
                  }}
                  endAdornment={
                    <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                      {propertyAddress && (
                        <IconButton
                          className="cpClearInputBtn"
                          size="small"
                          onClick={() => {
                            setPropertyAddress('');
                          }}
                          edge="end"
                          sx={{ right: '15px' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  }
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="addressMailWrapper">
              <Box className="selectAddress">
                <InputLabel className="addressLabel newfieldsLabel">
                  {MESSAGES.CONTACTS.ADVANCE_FILTERS.CONTACT_TYPE}
                </InputLabel>
                <MultiSelect
                  options={CONTACT_TYPE}
                  selectedValue={selectedContactType}
                  setSelectedValue={setSelectedContactType}
                  placeHolderText={MESSAGES.CONTACTS.ADVANCE_FILTERS.CONTACT_TYPE}
                  noOptionText={MESSAGES.CONTACTS.ADVANCE_FILTERS.NO_CONTACT_TYPE}
                  clearable={true}
                  // newDesign={true}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="activeMailingFilter">
              <InputLabel className="activeMailingLabel">
                {MESSAGES.CONTACTS.ADVANCE_FILTERS.MAILING_ACTIVITY}
              </InputLabel>
              <label className="activeMailingCheckbox">
                <input
                  type="checkbox"
                  checked={showActiveMailings}
                  onChange={(event) => setShowActiveMailings(event.target.checked)}
                />
                <span>{MESSAGES.CONTACTS.ADVANCE_FILTERS.ACTIVE_MAILING}</span>
              </label>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </OlcFormModal>
  );
});

export default AdvanceFilters;
